/**
 * Alpine.js Directive: x-image-comparison-slider
 *
 * Usage:
 * <div x-image-comparison-slider class="image-splitter">
 *   <div class="mover" data-initial-slider-position="70"></div>
 *   <div class="img-left"><img src="before.jpg" /></div>
 *   <div class="img-right"><img src="after.jpg" /></div>
 * </div>
 */

import Alpine from "alpinejs";

document.addEventListener("alpine:init", () => {
    // Slider Controller
    const sliderController = {
        sliders: new Map(),

        initializeSlider(el) {
            if (this.sliders.has(el)) {
                return;
            }

            const sliderData = {
                activeMover: null,
                mouseDownX: 0,
                lastX: 0,
                cta: null,
                mover: el.querySelector(".mover"),
                imgLeft: el.querySelector(".img-left"),
                splitter: el
            };

            if (!sliderData.mover || !sliderData.imgLeft) {
                return;
            }

            // Bind handlers
            const handleStart = this._handleStart.bind(this, sliderData);
            const handleMove = this._handleMove.bind(this, sliderData);
            const handleStop = this._handleStop.bind(this, sliderData);
            const handleResize = this._setSliderPositions.bind(this, sliderData);

            sliderData.handlers = { handleStart, handleMove, handleStop, handleResize };

            // Initialize position
            setTimeout(() => {
                this._setSliderPositions(sliderData);
            }, 0);

            // Add event listeners to the slider element (not document)
            el.addEventListener("mousedown", handleStart);
            el.addEventListener("touchstart", handleStart);
            document.addEventListener("mousemove", handleMove);
            document.addEventListener("touchmove", handleMove);
            document.addEventListener("mouseup", handleStop);
            document.addEventListener("touchend", handleStop);
            window.addEventListener("resize", handleResize);

            this.sliders.set(el, sliderData);
        },

        destroySlider(el) {
            const sliderData = this.sliders.get(el);
            if (!sliderData) return;

            const { handlers } = sliderData;
            el.removeEventListener("mousedown", handlers.handleStart);
            el.removeEventListener("touchstart", handlers.handleStart);
            document.removeEventListener("mousemove", handlers.handleMove);
            document.removeEventListener("touchmove", handlers.handleMove);
            document.removeEventListener("mouseup", handlers.handleStop);
            document.removeEventListener("touchend", handlers.handleStop);
            window.removeEventListener("resize", handlers.handleResize);

            this.sliders.delete(el);
        },

        _setSliderPositions(sliderData) {
            const { mover, imgLeft } = sliderData;
            const moverWidth = mover.getBoundingClientRect().width;
            const width = imgLeft.getBoundingClientRect().width;
            const height = 9999;
            const initialPosition = parseInt(mover.dataset.initialSliderPosition || 70) * 0.01;

            mover.style.left = width * initialPosition - moverWidth / 2 + "px";
            imgLeft.style.clip = `rect(0, ${width * initialPosition}px, ${height}px, 0px)`;
        },

        _handleStart(sliderData, e) {
            const { mover } = sliderData;
            if (!e.target.closest(".mover") && e.target !== mover) return;

            sliderData.activeMover = mover;
            sliderData.mouseDownX = 1;
            sliderData.lastX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
            document.body.classList.add("before-after-interacted");
        },

        _handleMove(sliderData, e) {
            if (!sliderData.mouseDownX) return;

            const { mover } = sliderData;
            const moverOffset = mover.getBoundingClientRect().width / 2;
            const currentX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;

            this._calculateMovement(sliderData, currentX, sliderData.lastX, moverOffset);
        },

        _handleStop(sliderData) {
            sliderData.mouseDownX = 0;
        },

        _calculateMovement(sliderData, currentX, previousX, moverOffset) {
            const { mover, imgLeft, splitter } = sliderData;
            const deltaMovement = currentX - previousX;
            const parentWidth = splitter.getBoundingClientRect().width;
            let leftPos = parseInt(mover.style.left) + deltaMovement;

            if (leftPos + moverOffset > parentWidth) {
                leftPos = parentWidth - moverOffset;
            } else if (leftPos + moverOffset <= 0) {
                leftPos = moverOffset * -1;
            }

            mover.style.left = leftPos + "px";
            sliderData.lastX = currentX;

            const clipRight = moverOffset + parseInt(mover.style.left);
            const height = mover.getBoundingClientRect().height;
            imgLeft.style.clip = `rect(0px, ${clipRight}px, ${height}px, 0px)`;
        }
    };

    // Register the directive
    Alpine.directive("image-comparison-slider", el => {
        sliderController.initializeSlider(el);

        // Return cleanup function
        return () => {
            sliderController.destroySlider(el);
        };
    });
});
